import { computed, unref, type MaybeRef, type MaybeRefOrGetter } from "vue";

import {
  RoomService,
  type IChatRoomFilter,
  type TChatRoom,
  type TChatMember,
} from "@/api/models/message/room";
import { formatName } from "@/utils/Formatters.ts";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import {
  useGenericPaginatedQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
} from "@/composables/shared/queries";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

// --------------------
// Query keys
// --------------------
export const RoomQueryKeys = createQueryKeys(["message", "room"], (key) => ({
  paginated: key<IChatRoomFilter>("paginated"),
  detail: key<string>("detail"),
}));

// --------------------
// Queries
// --------------------
export const useRoomsInfiniteQuery = (
  filters: MaybeRefOrGetter<Partial<IChatRoomFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericPaginatedQuery<TChatRoom, IChatRoomFilter>({
    queryKey: RoomQueryKeys.paginated(),
    params: { filters, enabled },
    fetcher: async (params) =>
      await RoomService.getAllRoomsPaginatedFiltered({}, params.filters, params.pageParam),
    permission: $permSubjects.MESSAGE__CHATROOM,
  });
};

export const useRoomQuery = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TChatRoom>({
    queryKey: RoomQueryKeys.detail(),
    params,
    fetcher: (id) => RoomService.getChatRoom({ id }),
    permission: $permSubjects.MESSAGE__CHATROOM,
    ...options,
  });
};

// --------------------
// Mutations
// --------------------
export const useAddChatRoomMembersMutation = (roomId: MaybeRef<string | undefined | null>) => {
  const roomIdRef = computed(() => unref(roomId) ?? undefined);

  return useGenericMutation<TChatRoom, string[]>({
    mutationFn: (staffIds) => {
      const rid = roomIdRef.value;
      if (!rid) throw new Error("useAddChatRoomMembersMutation: roomId is required");
      return RoomService.addChatRoomMembers({ id: rid }, { staff_members: staffIds });
    },
    sets: () => [RoomQueryKeys.detail(roomIdRef.value)],
    invalidates: () => [RoomQueryKeys.all(), RoomQueryKeys.detail(roomIdRef.value)],
    feature: "rooms.addMembers",
  });
};

export const useLeaveChatRoomMutation = (roomId: MaybeRef<string | undefined | null>) => {
  const roomIdRef = computed(() => unref(roomId) ?? undefined);

  return useGenericMutation<void, void>({
    mutationFn: () => {
      const rid = roomIdRef.value;
      if (!rid) throw new Error("useLeaveChatRoomMutation: roomId is required");
      return RoomService.leaveChatRoom({ id: rid }, undefined);
    },
    invalidates: () => [RoomQueryKeys.all()],
    removes: () => [RoomQueryKeys.detail(roomIdRef.value)],
    feature: "rooms.leave",
  });
};

export const useMuteChatRoomMutation = (roomId: MaybeRef<string | undefined | null>) => {
  const roomIdRef = computed(() => unref(roomId) ?? undefined);

  return useGenericMutation<void, void>({
    mutationFn: () => {
      const rid = roomIdRef.value;
      if (!rid) throw new Error("useMuteChatRoomMutation: roomId is required");
      return RoomService.muteChatRoom({ id: rid }, undefined);
    },
    invalidates: () => [RoomQueryKeys.all(), RoomQueryKeys.detail(roomIdRef.value)],
    feature: "rooms.mute",
  });
};

export const useUnmuteChatRoomMutation = (roomId: MaybeRef<string | undefined | null>) => {
  const roomIdRef = computed(() => unref(roomId) ?? undefined);

  return useGenericMutation<void, void>({
    mutationFn: () => {
      const rid = roomIdRef.value;
      if (!rid) throw new Error("useUnmuteChatRoomMutation: roomId is required");
      return RoomService.unmuteChatRoom({ id: rid }, undefined);
    },
    invalidates: () => [RoomQueryKeys.all(), RoomQueryKeys.detail(roomIdRef.value)],
    feature: "rooms.unmute",
  });
};

export const useDirectChatRoomMutation = () => {
  return useGenericMutation<TChatRoom, string>({
    mutationFn: (staffId) => RoomService.createDirectChatRoom({}, { staff_recipient: staffId }),
    sets: (data) => [RoomQueryKeys.detail(data.id)],
    invalidates: [RoomQueryKeys.all()],
    feature: "rooms.direct",
  });
};

export const useGroupChatRoomMutation = () => {
  return useGenericMutation<TChatRoom, string[]>({
    mutationFn: (staffIds) => RoomService.createGroupChatRoom({}, { staff_members: staffIds }),
    sets: (data) => [RoomQueryKeys.detail(data.id)],
    invalidates: [RoomQueryKeys.all()],
    feature: "rooms.group",
  });
};

export type TMemberDictValue = {
  persona: string;
  staff?: string;
  patient?: string;
  name: string;
};

export type TMemberDict = Record<string, TMemberDictValue>;

export const buildMemberDict = (members: TChatMember[]): TMemberDict => {
  const dict: TMemberDict = {};
  for (const m of members) {
    dict[m.persona] = {
      persona: m.persona,
      staff: m.staff,
      patient: m.patient,
      name: formatName(m),
    };
  }
  return dict;
};
